import { useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

// Services
import UserService from "../../../../../services/UserService";
import DocumentService from "../../../../../services/DocumentService";


// Components
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { Box, Button, CircularProgress, IconButton, Link, Stack, TextField, Typography } from "@mui/material";

// Icons
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const UserDocumentRow = ({ row, refreshDocuments }) => {
  const [isOpeningLoading, setIsOpeningLoading] = useState(false);
  const [isRefusingLoading, setIsRefusingLoading] = useState(false);
  const [isAcceptingLoading, setIsAcceptingLoading] = useState(false);
  const [isExpireDateEditing, setIsExpireDateEditing] = useState(false);
  const [isDocumentEditing, setIsDocumentEditing] = useState(false);
  const [expireDate, setExpireDate] = useState(
    row?.document?.expire_at ? dayjs(row?.document?.expire_at).format("YYYY-MM-DD") : ""
  );
  const [isEditLoading, setIsEditLoading] = useState(false);

  const document = row?.document;
  const createdAt = document?.created_at ? dayjs.utc(document.created_at).format("DD/MM/YYYY") : "--";
  const expireAt = document?.expire_at ? dayjs.utc(document.expire_at).format("DD/MM/YYYY") : "--";
  const updatedAt = document?.updated_at ? dayjs.utc(document.updated_at).format("DD/MM/YYYY à HH:MM") : "--";
  const admin = document?.admin ? document.admin : "--";
  const adminName = !admin ? "--" : `${admin?.lastname?.charAt(0).toUpperCase()}. ${admin?.firstname}`;

  const handleOpenDocument = async () => {
    if (!document?.id) return;
    setIsOpeningLoading(true);
    try {
      const response = await DocumentService.getDocumentPdfBlog(document.id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url);
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 500);
    } catch (error) {
      console.error("Failed to open document:", error);
    } finally {
      setIsOpeningLoading(false);
    }
  };

  const handleAccept = async () => {
    setIsAcceptingLoading(true);
    try {
      await UserService.acceptDocument(document?.id);
      if (refreshDocuments) refreshDocuments();
    } catch (e) {
      console.error(e);
    } finally {
      setIsAcceptingLoading(false);
    }
  };

  const handleRefuse = async () => {
    setIsRefusingLoading(true);
    try {
      await UserService.refuseDocument(document?.id);
      if (refreshDocuments) refreshDocuments();
    } catch (e) {
      console.error(e);
    } finally {
      setIsRefusingLoading(false);
    }
  };

  const handleStartEditing = () => {
    setIsExpireDateEditing(true);
  };

  const handleCloseEditing = () => {
    setExpireDate(row?.document?.expire_at ? dayjs(row?.document?.expire_at).format("YYYY-MM-DD") : "");
    setIsExpireDateEditing(false);
  };

  const handleSaveEditing = async () => {
    setIsExpireDateEditing(false);
    setIsEditLoading(true);
    try {
      await UserService.updateDocumentExpireAt(document?.id, expireDate);
      if (refreshDocuments) refreshDocuments();
    } catch (e) {
      console.error(e);
    } finally {
      setIsEditLoading(false);
    }
  };

  return (
    <TableRow key={row.id} sx={{ opacity: document ? 1 : 0.7, background: document ? "#FCFDFD" : "white" }}>
      <TableCell size="small">
        {document ? (
          <Link
            sx={{
              color: "primary",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              textDecoration: "none",
              cursor: "pointer",
            }}
            onClick={handleOpenDocument}
          >
            {row.name} {isOpeningLoading ? <CircularProgress size={16} /> : <OpenInNewIcon sx={{ fontSize: 16 }} />}
          </Link>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{
              color: "text.primary",
              fontWeight: 500,
            }}
          >
            {row.name}
          </Stack>
        )}
      </TableCell>
      <TableCell size="small">{createdAt}</TableCell>
      <TableCell size="small">
        <Stack direction="row" alignItems="center" gap={0.5}>
          {isExpireDateEditing ? (
            <TextField
              size="small"
              type="date"
              value={expireDate}
              inputProps={{ sx: { padding: "8px !important" } }}
              onChange={(e) => setExpireDate(e.target.value)}
            />
          ) : (
            expireAt
          )}
          {isExpireDateEditing ? (
            <>
              <IconButton onClick={handleSaveEditing} disabled={isEditLoading}>
                {isEditLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <CheckIcon sx={{ width: 16, height: 16, color: "success.main" }} />
                )}
              </IconButton>
              <IconButton onClick={handleCloseEditing} disabled={isEditLoading}>
                <CloseIcon sx={{ width: 16, height: 16, color: "error.main" }} />
              </IconButton>
            </>
          ) : (
            <IconButton size="small" color="primary" onClick={handleStartEditing}>
              <EditIcon sx={{ width: 16, height: 16 }} />
            </IconButton>
          )}
        </Stack>
      </TableCell>
      <TableCell size="small" align="right">
        {!document ? (
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>Document manquant</Typography>
        ) : (
          <>
            <Box
              sx={{
                fontSize: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
                gap: 0.5,
              }}
            >
              {document?.status == 2 && (
                <Typography
                  sx={{
                    color: "error.main",
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "end",
                    gap: 0.5,
                  }}
                >
                  Refusé par <b>{adminName}</b> le <b>{updatedAt}</b>
                  <CloseIcon sx={{ color: "error.main", fontSize: 18 }} />
                </Typography>
              )}
              {document?.status == 1 && (
                <Typography
                  sx={{
                    color: "success.main",
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "end",
                    gap: 0.5,
                  }}
                >
                  Accepté par <b>{adminName}</b> le <b>{updatedAt}</b>
                  <CheckIcon sx={{ color: "success.main", fontSize: 18 }} />
                </Typography>
              )}
              {!document?.status == 0 && (
                <>
                  {" "}
                  {isDocumentEditing ? (
                    <IconButton size="small" color="primary" onClick={() => setIsDocumentEditing(false)}>
                      <CloseIcon sx={{ width: 16, height: 16 }} />
                    </IconButton>
                  ) : (
                    <IconButton size="small" color="primary" onClick={() => setIsDocumentEditing(true)}>
                      <EditIcon sx={{ width: 16, height: 16 }} />
                    </IconButton>
                  )}
                </>
              )}
            </Box>
            {(document?.status == 0 || isDocumentEditing) && (
              <Stack direction="row" alignItems="center" justifyContent="end" spacing={1}>
                <Button
                  startIcon={!isAcceptingLoading ? <CheckIcon size={20} /> : <CircularProgress size={16} />}
                  size="small"
                  color="success"
                  variant="outlined"
                  disabled={isAcceptingLoading || isRefusingLoading}
                  onClick={handleAccept}
                >
                  Valider
                </Button>
                <Button
                  startIcon={!isRefusingLoading ? <CloseIcon size={20} /> : <CircularProgress size={16} />}
                  size="small"
                  color="error"
                  variant="outlined"
                  disabled={isAcceptingLoading || isRefusingLoading}
                  onClick={handleRefuse}
                >
                  Refuser
                </Button>
                <Button
                  startIcon={isOpeningLoading ? <CircularProgress size={16} /> : <OpenInNewIcon />}
                  size="small"
                  color="primary"
                  variant="outlined"
                  onClick={handleOpenDocument}
                  disabled={isOpeningLoading}
                >
                  Consulter
                </Button>
              </Stack>
            )}
          </>
        )}
      </TableCell>
    </TableRow>
  );
};

export default UserDocumentRow;
